import React from "react";
import { motion } from "framer-motion";
import Nav from "../components/nav";
import SEO from '../components/SEO';
import { baseUrl } from '../config';



const stages = [
    {
        title: "Pre-Production",
        description: "Leverage advanced AI tools for intelligent scriptwriting, precise scheduling, optimized budgeting, and strategic resource planning. Reduce planning time by 60% and minimize human error.",
        image: "/api/placeholder/300/200?text=Pre-Production",
        highlights: [
            "AI Script Analysis",
            "Smart Scheduling",
            "Budget Optimization",
            "Resource Allocation"
        ]
    },
    {
        title: "Production",
        description: "Real-time AI monitoring of on-set workflows, intelligent equipment management, and dynamic problem-solving. Ensure seamless execution and minimize unexpected delays.",
        image: "/api/placeholder/300/200?text=Production",
        highlights: [
            "Live Set Monitoring",
            "Equipment Tracking",
            "Workflow Optimization",
            "Instant Issue Resolution"
        ]
    },
    {
        title: "Post-Production",
        description: "AI-powered editing tools, automated visual effects, intelligent sound design, and rapid content delivery. Transform complex post-production into a streamlined, efficient process.",
        image: "/api/placeholder/300/200?text=Post-Production",
        highlights: [
            "Smart Editing",
            "VFX Automation",
            "Sound Optimization",
            "Quick Rendering"
        ]
    }
];

const ProducerBuddyGuidePage = () => {
    return (
        <>
        <SEO
          title="All-in-One Film Production Tools"
          description="All the tools a filmmaker needs in one platform: script analysis, budget planning, casting, location scouting, editing, and film distribution — powered by ProducerBuddy."
          url="https://producerbuddy.pilomation.com/all-in-one-tools"
        />
        <Nav/>
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 text-black">

            {/* Hero Section */}
            <section className="min-h-screen flex items-center px-16">
                <div className="w-1/2 space-y-6">
                    <motion.h1
                        initial={{ x: -50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-white"
                    >
                        AI-Powered Film Production Companion
                    </motion.h1>
                    <motion.p 
                        initial={{ x: -50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 1 }}
                        className="text-xl text-black"
                    >
                        Streamline every stage of film production with intelligent AI technology. From pre-production planning to final editing, Producer Buddy transforms your workflow.
                    </motion.p>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-3 rounded-full text-white font-bold shadow-lg hover:shadow-xl transition"
                    >
                        Start Your Production Journey
                    </motion.button>
                </div>
                
                <div className="w-1/2 flex justify-center relative">
                    <div className="w-96 h-[600px] bg-black/30 rounded-2xl overflow-hidden border-4 border-white/20">
                        {stages.map((stage, index) => (
                            <motion.div 
                                key={stage.title}
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.3, duration: 0.6 }}
                                className="absolute w-full h-1/3 flex items-center p-6"
                                style={{ top: `${index * 33.33}%` }}
                            >
                                <div className="w-full h-full bg-white/10 backdrop-blur-sm rounded-xl flex items-center p-4 space-x-6">
                                    <img 
                                        src={stage.image} 
                                        alt={stage.title} 
                                        className="w-36 h-24 object-cover rounded-lg"
                                    />
                                    <div>
                                        <h3 className="text-xl font-bold text-cyan-300">{stage.title}</h3>
                                        <div className="flex space-x-2 mt-2">
                                            {stage.highlights.map((highlight) => (
                                                <span 
                                                    key={highlight} 
                                                    className="bg-white/20 px-2 py-1 rounded-full text-xs text-black"
                                                >
                                                    {highlight}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stages Detail Section */}
            <section className="px-16 py-16 space-y-12">
                {stages.map((stage, index) => (
                    <motion.div 
                        key={stage.title}
                        initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className={`flex items-center space-x-12 ${index % 2 !== 0 ? 'flex-row-reverse' : ''}`}
                    >
                        <div className="w-1/2">
                            <h2 className="text-4xl font-bold text-cyan-300 mb-6">{stage.title}</h2>
                            <p className="text-lg text-black mb-6">{stage.description}</p>
                            <div className="flex flex-wrap gap-3">
                                {stage.highlights.map((highlight) => (
                                    <span 
                                        key={highlight} 
                                        className="bg-white/20 px-4 py-2 rounded-full text-sm text-black"
                                    >
                                        {highlight}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="w-1/2">
                            <img 
                                src={stage.image} 
                                alt={stage.title} 
                                className="w-full rounded-2xl shadow-2xl"
                            />
                        </div>
                    </motion.div>
                ))}
            </section>
        </div>
        </>
    );
};

export default ProducerBuddyGuidePage;
